import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import { UserFourIcon } from '@repo/ui/icons'
import ProgressBar from '@repo/ui/progress-bar'
import LineTab from '@repo/ui/tabs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { getStaff } from '../../api/staff/get-staff'
import { getStaffMissingDocuments } from '../../api/staff/get-staff-missing-documents'
import { getStaffProfileProgress } from '../../api/staff/staff-profile-progress'
import { updateStaff } from '../../api/staff/staff-update'
import { getMe } from '../../api/user/get-me'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import StaffMissingDocumentsContainer from '../../components/pages/staff/staff-missing-documents-container'
import { getStaffProfileMissingInformationItems } from '../../components/pages/staff/utils/staff-profile-missing-information'
import StaffPersonalSpaceBasicInformation from '../../components/pages/staff-personal-space/staff-personal-space-basic-information'
import StaffPersonalSpaceForm from '../../components/pages/staff-personal-space/staff-personal-space-form'
import useAuth from '../../lib/hooks/use-auth'
import useTranslate from '../../lib/hooks/use-translate'
import { StaffDocumentTypeEnum } from '../../lib/types/enums/common/document-status.enum'
import { StaffMissingDocument } from '../../lib/types/models/staff/staff'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { mapStaffToInputPersonalSpace, updateStaffDataMapping } from './utils'

const StaffProfilePage = () => {
  const [activeTab, setActiveTab] = useState(0)

  const { t } = useTranslate()

  const { user, setUser } = useAuth()
  const queryClient = useQueryClient()

  const staffId = user?.staff?.id

  if (!staffId) return null

  const { data: staffProfileProgress, isPending: progressLoading } = useQuery({
    queryKey: ['staff-profile-progress', staffId],
    queryFn: () => getStaffProfileProgress(staffId)
  })

  const { data: missingDocuments, isPending: missingDocumentLoading } = useQuery({
    queryKey: ['staff-missing-documents', staffId],
    queryFn: () => {
      if (staffId && !isNaN(staffId)) return getStaffMissingDocuments(staffId)
    }
  })
  const { data: staff, isPending: loadingStaff } = useQuery({
    queryKey: ['staff', staffId],
    queryFn: () => getStaff(staffId)
  })

  const { mutate: updateStaffMutation, isPending: isLoadingSubmission } = useMutation({
    mutationFn: updateStaff,
    onSuccess: (_, { picture }) => {
      toast.success('Vos informations personnelles ont été mises à jour')
      // The header user menu shows the picture sent by the backend with the connected user
      if (picture) getMe().then(setUser).catch(showAxiosError)
      queryClient.invalidateQueries({
        exact: true,
        queryKey: ['staff', staffId]
      })

      queryClient.invalidateQueries({
        exact: true,
        queryKey: ['staff-profile-progress', staffId]
      })

      queryClient.invalidateQueries({
        exact: true,
        queryKey: ['staff-missing-documents', staffId]
      })
    },
    onError: showAxiosError
  })

  const scrollToElement = (elementId: string) => {
    const element = window.document.getElementById(elementId)
    if (!element) return

    const container = window.document.getElementById('layout-data')
    if (!container) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }

    const containerRect = container.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()
    const targetTop = elementRect.top - containerRect.top + container.scrollTop - 10
    const maxScrollTop = container.scrollHeight - container.clientHeight

    window.scrollTo({ top: 0, left: 0 })
    container.scrollTo({
      top: Math.min(Math.max(targetTop, 0), maxScrollTop),
      behavior: 'smooth'
    })
  }

  const scrollToMissingDocument = (document: StaffMissingDocument) => {
    // Map document types to their respective element IDs
    const elementIds: Partial<Record<StaffDocumentTypeEnum, string>> = {
      [StaffDocumentTypeEnum.CRIMINAL_RECORD]: 'criminal-record',
      [StaffDocumentTypeEnum.ID_FILE]: 'id-card',
      [StaffDocumentTypeEnum.PAY_SLIP]: 'pay-slip',
      [StaffDocumentTypeEnum.ETA]: 'eta-card'
    }

    let elementId: string | undefined

    // Handle special case for `DIPLOMA` where we need to use a property on the document
    if (document.type === StaffDocumentTypeEnum.DIPLOMA && 'diploma' in document) {
      elementId = document.diploma.type // Dynamically assign based on diploma type
    } else {
      elementId = elementIds[document.type]
    }

    if (elementId) scrollToElement(elementId)
  }

  if (!staff || progressLoading || missingDocumentLoading || loadingStaff) return <LoadingScreen />

  const scrollToMissingInformationElement = (elementId: string) => {
    scrollToElement(elementId)
  }

  const scrollToMissingInformation = (elementId: string) => {
    if (activeTab === 0) {
      scrollToMissingInformationElement(elementId)
      return
    }

    setActiveTab(0)
    setTimeout(() => {
      scrollToMissingInformationElement(elementId)
    }, 100)
  }

  const additionalMissingInformation = getStaffProfileMissingInformationItems(staff, () => {
    scrollToMissingInformation('staff-personal-space-bank-information')
  }).map((item) =>
    item.label === 'staff.personal.space.missing.emergency.contact.details'
      ? {
          ...item,
          onClick: () => {
            scrollToMissingInformation('staff-personal-space-emergency-contact')
          }
        }
      : item
  )

  return (
    <div className="flex flex-col gap-5">
      <UserNavBar
        title={t('staff.personal.space.personal.info')}
        icon={(props) => (
          <UserFourIcon {...props} color={tailwindColors.fushia.DEFAULT} filled={false} />
        )}
      />
      <Box className="px-5 py-6">
        <LineTab
          wrapperClassName="h-fit"
          tabClassName="mobile:w-full"
          tabTextVariant="tag"
          activeTabColor={tailwindColors.secondaryRed.DEFAULT}
          tabs={[
            {
              label: t('staff.personal.space.to.complete'),
              component: (
                <Box className="bg-white-active rounded-lg p-5 mt-4">
                  <ProgressBar
                    text={t('staff.personal.space.progress.percentage')}
                    progress={staffProfileProgress ?? 0}
                    colorStart={tailwindColors.secondary.DEFAULT}
                    colorEnd={tailwindColors.primary.DEFAULT}
                  />
                </Box>
              )
            },
            { label: t('staff.personal.space.my.profile') }
          ]}
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab)
          }}
        />
        <div className="h-4" />

        {((missingDocuments && missingDocuments.length > 0) ||
          additionalMissingInformation.length > 0) && (
          <StaffMissingDocumentsContainer
            data={missingDocuments ?? []}
            additionalMissingInformation={additionalMissingInformation}
            onMissingDocumentClick={(document) => {
              if (activeTab === 0) {
                scrollToMissingDocument(document)
              } else {
                setActiveTab(0)
                setTimeout(() => {
                  scrollToMissingDocument(document)
                }, 100)
              }
            }}
          />
        )}
      </Box>
      {activeTab === 0 ? (
        <StaffPersonalSpaceForm
          disabled={!staff.canModify}
          staff={staff}
          defaultValues={staff ? mapStaffToInputPersonalSpace(staff) : undefined}
          handleSubmit={(input) => {
            const payloadRecord = updateStaffDataMapping(input, staff.canModify)

            updateStaffMutation({
              id: staffId,
              ...payloadRecord
            })
          }}
          isLoading={isLoadingSubmission}
          onUpdateProfilePicture={(picture) => {
            updateStaffMutation({ id: staffId, picture })
          }}
        />
      ) : (
        <StaffPersonalSpaceBasicInformation
          disabled={!staff.canModify}
          staff={staff}
          onUpdateProfilePicture={(picture) => {
            updateStaffMutation({ id: staffId, picture })
          }}
        />
      )}
    </div>
  )
}

export default StaffProfilePage
